export type CytoQCStatus = "pass" | "fail";

const isValid = (v: number) =>
  v !== null && v !== undefined && !isNaN(v) && v > 0;

// --- Dispositioning metrics (affect pass/fail) ---

interface DispositionMetric {
  key: string;
  label: string;
  threshold: string;
  check: (v: number) => boolean; // returns true if passing
}

export const DISPOSITION_METRICS: DispositionMetric[] = [
  {
    key: "assignedRate",
    label: "Assigned Rate",
    threshold: "Fail ≤ 65%",
    check: (v) => v > 65,
  },
  {
    key: "extracellularRatio",
    label: "Extracellular Ratio",
    threshold: "Fail ≥ 25%",
    check: (v) => v < 0.25,
  },
  {
    key: "nucleatedPct",
    label: "Percent Nucleated",
    threshold: "Fail ≤ 90%",
    check: (v) => v > 90,
  },
  {
    key: "assignedPerMm2",
    label: "Cell Assigned per mm²",
    threshold: "Fail ≤ 1M counts/mm²",
    check: (v) => v > 1000000,
  },
];

export const RUN_PASS_THRESHOLD = 0.75;

// --- Well status ---

export function getWellStatus(
  well: {
    assignedRate: number;
    extracellularRatio: number;
    nucleatedPct: number;
    assignedPerMm2: number;
  },
  skipMetrics?: string[]
): CytoQCStatus {
  for (const metric of DISPOSITION_METRICS) {
    if (skipMetrics?.includes(metric.key)) continue;
    const val = (well as Record<string, number>)[metric.key];
    if (isValid(val) && !metric.check(val)) return "fail";
  }
  return "pass";
}

export function getWellStatusFromBatches(
  well: { confluency: number; nucleatedPct: number },
  batches: {
    assignedRate: number;
    extracellularRatio: number;
    assignedCountsPerMm2: number;
  }[]
): CytoQCStatus {
  if (!isValid(well.nucleatedPct) || well.nucleatedPct <= 90) {
    if (isValid(well.nucleatedPct)) return "fail";
  }

  const validBatches = batches.filter(
    (b) =>
      isValid(b.assignedRate) ||
      isValid(b.extracellularRatio) ||
      isValid(b.assignedCountsPerMm2)
  );

  for (const b of validBatches) {
    if (isValid(b.assignedRate) && b.assignedRate <= 65) return "fail";
    if (isValid(b.extracellularRatio) && b.extracellularRatio >= 0.25)
      return "fail";
    if (isValid(b.assignedCountsPerMm2) && b.assignedCountsPerMm2 <= 1000000)
      return "fail";
  }

  return "pass";
}

// --- Run status ---

export function getRunStatus(wellStatuses: CytoQCStatus[]): CytoQCStatus {
  if (wellStatuses.length === 0) return "pass";
  const failCount = wellStatuses.filter((s) => s === "fail").length;
  const passRate = 1 - failCount / wellStatuses.length;
  return passRate >= RUN_PASS_THRESHOLD ? "pass" : "fail";
}

// --- Metric color (disposition metrics only affect pass/fail) ---

export function getDispositionColor(
  value: number,
  metric: string
): string | undefined {
  if (!isValid(value)) return undefined;
  const def = DISPOSITION_METRICS.find((m) => m.key === metric);
  if (!def) return undefined;
  return def.check(value) ? "#4caf50" : "#f44336";
}

export function getBatchAssignedColor(value: number): string | undefined {
  if (!isValid(value)) return undefined;
  return value >= 100000 && value <= 500000 ? "#4caf50" : "#f44336";
}

// --- UI constants ---

export const STATUS_COLORS: Record<CytoQCStatus, string> = {
  pass: "#4caf50",
  fail: "#f44336",
};

export const WELL_BG_COLORS: Record<CytoQCStatus, string> = {
  pass: "#2e7d32",
  fail: "#c62828",
};

export { isValid };
